import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { splitRareSenses, cardPos } from '@shared/lib';
import type { Word, WordMeaning } from '@shared/types';
import { splitExample } from '../../lib/highlight';
import { useSessionSenses } from '../../model/useSessionSenses';
import { SessionPill, SessionSenses } from '../SessionSenses/SessionSenses';

interface WordPreviewProps {
  word: Word;
  /** Подпись части речи (например «прил.»); по умолчанию — как пришла с API. */
  posLabel?: (pos: string) => string;
  /** Вместо разбора слова — предупреждение (карточка требует действия). */
  alert?: ReactNode;
  /** Нижняя строка карточки: статус и действие. */
  footer?: ReactNode;
  /** Убрать слово из модуля. Есть — карточки слова удаляются по одной (последняя
   *  убирает слово); нет — только просмотр. */
  onDeleteWord?: () => void;
  /** Смысл под примером у каждой карточки слова — окно слова на «Словах и модулях» (2026-10-28). */
  showDefinition?: boolean;
}

/** Значение карточки: выбранная часть речи (meaningId), иначе первая с разбором. */
function pickMeaning(word: Word): WordMeaning | undefined {
  const meanings = word.meanings ?? [];
  return (
    (word.meaningId && meanings.find((m) => m.id === word.meaningId && m.senses.length > 0)) ||
    meanings.find((m) => m.senses.length > 0)
  );
}

/**
 * Своё разобранное слово: его карточки — каждый выбранный перевод. Отдельный компонент
 * с key по state — список, раскрытые блоки и подтверждение удаления не переезжают на
 * соседнее слово при листании.
 */
function WordSensesPanel({
  word,
  meaning,
  pos,
  footer,
  onDeleteWord,
  showDefinition,
}: {
  word: Word;
  meaning: WordMeaning;
  pos: string | null;
  footer?: ReactNode;
  onDeleteWord?: () => void;
  showDefinition?: boolean;
}) {
  // Ничего не отмечено на сервере — карточка одна, основной смысл (как в очереди создания).
  const mainSenseId = meaning.senses.find((s) => !s.isRare)?.id;
  const serverIds = word.sessionSenseIds?.length ? word.sessionSenseIds : mainSenseId ? [mainSenseId] : [];
  const { ids, remove } = useSessionSenses(word.stateId, serverIds, { onRemoveLast: onDeleteWord });
  const checked = meaning.senses.filter((s) => !s.isRare && ids.includes(s.id)).length;
  return (
    <article className="wb-preview wb-preview--senses">
      <div className="wb-preview-top">
        <div className="min-w-0">
          <div className="wb-preview-word" lang="en">{word.word}</div>
          {(word.ipa || pos) && (
            <div className="wb-preview-sub">
              {word.ipa && <span>{word.ipa}</span>}
              {pos && <span className="wb-preview-pos">{pos}</span>}
            </div>
          )}
        </div>
        <SessionPill count={Math.max(checked, 1)} className="wb-preview-pill" />
      </div>
      <SessionSenses
        word={word.word}
        senses={meaning.senses}
        cardIds={ids}
        onRemove={remove}
        variant="panel"
        showDefinition={showDefinition}
      />
      {footer && <div className="wb-preview-foot">{footer}</div>}
    </article>
  );
}

/** Карточка слова: слово, транскрипция, переводы, значение, пример и подсказка. */
export function WordPreview({ word, posLabel, alert, footer, onDeleteWord, showDefinition }: WordPreviewProps) {
  const { t } = useTranslation();
  const meaning = pickMeaning(word);
  const shownPos = cardPos(meaning?.pos, word.isPhrasalVerb);
  const pos = shownPos ? (posLabel ? posLabel(shownPos) : shownPos) : null;

  // Своё слово с разбором: список его карточек. Чужое (публичный модуль) и требующее
  // действия — прежний вид.
  if (
    !alert && word.stateId && meaning && meaning.id === word.meaningId &&
    meaning.senses.some((s) => !s.isRare)
  ) {
    return (
      <WordSensesPanel
        key={word.stateId}
        word={word}
        meaning={meaning}
        pos={pos}
        footer={footer}
        onDeleteWord={onDeleteWord}
        showDefinition={showDefinition}
      />
    );
  }

  // «Редкие» — не значение, а остаток строки словаря: отдельной строкой, не чипами.
  const { senses, rare } = splitRareSenses(meaning?.senses ?? []);
  const translations = [
    ...new Set(senses.flatMap((s) => s.translations.map((tr) => tr.text.trim())).filter(Boolean)),
  ];
  const chips = translations.length > 0 ? translations : word.ru ? [word.ru] : [];
  const definition = senses.find((s) => s.definition?.trim())?.definition?.trim();
  const exampleEn = senses.find((s) => s.exampleEn?.trim())?.exampleEn?.trim();
  const hint = senses.find((s) => s.hint?.trim())?.hint?.trim();
  const example = exampleEn ? splitExample(exampleEn, word.word) : null;

  return (
    <article className="wb-preview">
      <div className="wb-preview-word" lang="en">{word.word}</div>
      {alert ? (
        <div className="wb-alert">{alert}</div>
      ) : (
        <>
          {(word.ipa || pos) && (
            <div className="wb-preview-sub">
              {word.ipa && <span>{word.ipa}</span>}
              {pos && <span className="wb-preview-pos">{pos}</span>}
            </div>
          )}
          {chips.length > 0 && (
            <ul className="wb-chips">
              {chips.map((text, i) => (
                <li key={`${text}-${i}`} className="wb-chip">{text}</li>
              ))}
            </ul>
          )}
          {(definition || example || hint || rare.length > 0) && (
            <dl className="wb-facts">
              {definition && (
                <div className="wb-fact">
                  <dt>{t('wordBrowser.meaning')}</dt>
                  <dd>{definition}</dd>
                </div>
              )}
              {example && (
                <div className="wb-fact wb-fact--example">
                  <dt>{t('wordBrowser.example')}</dt>
                  <dd lang="en">
                    {example.pre}
                    {example.word && <b>{example.word}</b>}
                    {example.post}
                  </dd>
                </div>
              )}
              {hint && (
                <div className="wb-fact wb-fact--hint">
                  <dt>{t('wordBrowser.hint')}</dt>
                  <dd>{hint}</dd>
                </div>
              )}
              {rare.length > 0 && (
                <div className="wb-fact">
                  <dt>{t('wordBrowser.rare')}</dt>
                  <dd>{rare.join(', ')}</dd>
                </div>
              )}
            </dl>
          )}
        </>
      )}
      {footer && <div className="wb-preview-foot">{footer}</div>}
    </article>
  );
}
